import type { AgentCoachResponse } from '@on3/api';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import clsx from 'clsx';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';

import styles from './CoachAgentModule.module.scss';

interface ICoachAgentModuleProps {
  coachAgent: AgentCoachResponse;
}

export const CoachAgentModule = ({ coachAgent }: ICoachAgentModuleProps) => {
  const { agency, agent, secondaryAgent, coaches } = coachAgent ?? {};
  const filteredCoaches = coaches
    ?.filter(
      (coach, index, arr) =>
        index === arr.findIndex((c) => c.key === coach.key),
    )
    .slice(0, 3);
  const isTwoCoachesOrLess = filteredCoaches && filteredCoaches.length <= 2;

  return (
    <div className={styles.agentContainer}>
      <div className={styles.agentWrapper}>
        <Text className={styles.agentLabel} variant="subtitle1">
          Agent:
        </Text>
        <Text>{agent?.name}</Text>
        {secondaryAgent && <Text>{secondaryAgent?.name}</Text>}
        <Text className={styles.agencyName}>{agency?.name}</Text>
      </div>
      {!!coaches?.length && (
        <div className={styles.representWrapper}>
          <Text className={styles.representTitle} variant="subtitle1">
            Also Representing
          </Text>
          <div
            className={clsx(styles.clientWrapper, {
              [styles.multiCoachWrapper]: isTwoCoachesOrLess,
            })}
          >
            {filteredCoaches?.map((coach) => {
              const {
                key,
                defaultAsset,
                slug,
                fullName,
                primaryPosition,
                organization,
                age,
                salary,
              } = coach;

              return (
                <div className={styles.clientItems} key={key}>
                  <On3Avatar
                    altText={defaultAsset?.altText}
                    className={styles.avatar}
                    domain={defaultAsset?.domain}
                    height={50}
                    source={defaultAsset?.source}
                    width={50}
                  />
                  <div className={styles.infoWrapper}>
                    <TextLink href={`/db/coach/${slug}/`} variant="subtitle2">
                      {fullName}
                    </TextLink>
                    <Text className={styles.info} component="div">
                      {primaryPosition && (
                        <span>
                          {primaryPosition?.abbr}
                          <span className={styles.slash}>&#47;</span>
                        </span>
                      )}
                      {organization?.slug && (
                        <>
                          <TeamAvatar
                            height={18}
                            slug={organization?.slug}
                            src={organization?.assetUrl}
                            width={18}
                          />
                          <span className={styles.slash}>&#47;</span>
                        </>
                      )}
                      {age && (
                        <span>
                          Age: {age}
                          <span className={styles.slash}>&#47;</span>
                        </span>
                      )}
                      {salary && (
                        <span className={styles.salary}>
                          {formatCurrency(salary)}
                        </span>
                      )}
                      <span />
                    </Text>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
